import ApplicationController from 'controllers/application-controller'
import lax from 'lib/wrapped-lax'
import { useIntersection } from 'stimulus-use'
import Rect from 'lib/rect'
import MediaQuery from 'lib/media-query'
import debounce from 'lodash/debounce'
import lozad from 'lozad'

export default class ScrollProgressBarController extends ApplicationController {
  static inertia = -10

  // checkpoint(dots) targets count should eq anchor (blocks) targets count
  static targets = ['scale', 'pointer', 'checkpoint', 'indicator', 'anchor', 'scaleEnd']
  static values = {
    // align point using anchor dimensions (top, bottom, center) default: center
    // can-be redefined for specific anchor element using data-align attribute
    align: String,
    // outside element selector if we want trigger preInit in advance
    preInitElementSelector: String
  }

  constructor (context) {
    super(context)
    this.refreshAll = debounce(() => this.#refreshAll(), 1000)
    this.unobservePreInit = () => {}
  }

  connect () {
    super.connect()
    Rect.properties.indexOf(this.align) < 0 && console.error('invalid align value')
    this.#setupLax()
    this.#setupPreInit()
    useIntersection(this)
  }

  appear () {
    if (this.appeared) return

    this.appeared = true
    this.#initLozad()
    this.#initLax()
    this.#initMediaQueryListener()
    this.unobservePreInit()
  }

  disappear () {
    if (!this.appeared) return

    this.appeared = false
    lax.stop()
  }

  #setupLax () {
    lax.addDriver('scrollY', function () {
      return window.scrollY
    }, { inertiaEnabled: true, frameStep: 2 })
    this.processedScrollY = 0
  }

  #setupPreInit () {
    if (!this.preInitElement) return

    const [, unobserve] = useIntersection(this, { element: this.preInitElement })
    this.unobservePreInit = unobserve
  }

  #initLozad () {
    if (this.lozadInitialized) return

    this.lozadInitialized = true
    const observer = lozad('.scroll-progress-bar-lazy', {
      loaded: el => {
        el.classList.add('lazy-loaded')
        this.refreshAll()
      }
    })

    this.element.querySelectorAll('.scroll-progress-bar-lazy').forEach(observer.triggerLoad)
  }

  #initMediaQueryListener () {
    if (this.mediaQueryListenerInitialized) return

    this.mediaQueryListenerInitialized = true
    MediaQuery.addListener('change', this.refreshAll)
  }

  #refreshAll () {
    this.#storeDimensions()
    this.#updateScaleDimensions()
    this.#updateCheckpointsPositions()
    this.#replaceLaxElements()
  }

  #storeDimensions () {
    this.rect = new Rect(this.element)
    this.checkpointRects = []
    this.anchorRects = []
    this.anchorTargets.forEach((anchor, index) => {
      this.anchorRects.push(new Rect(anchor, this.rect))
      this.checkpointRects.push(new Rect(this.checkpointTargets[index], this.rect))
    })
  }

  #updateScaleDimensions () {
    this.scaleTarget.style.top = `${this.scaleStartPx}px`
    this.scaleTarget.style.height = `${this.scaleEndPx}px`
    this.scaleTarget.style.opacity = '1'
  }

  #updateCheckpointsPositions () {
    this.anchorRects.forEach((anchorRect, index) => {
      const checkpoint = this.checkpointTargets[index]
      checkpoint.style.top = `${anchorRect[this.align]}px`
      checkpoint.style.opacity = '1'
    })
  }

  #initLax () {
    lax.start()
  }

  #replaceLaxElements () {
    lax.removeElement(this.pointerTarget)
    lax.addElement(this.pointerTarget, {}, { onUpdate: this.handleProgressBarUpdate.bind(this) })
    this.indicatorTargets.forEach(checkpoint => {
      lax.removeElement(checkpoint)
      lax.addElement(checkpoint, {
        scrollY: {
          opacity: [
            ['elCenterY-elHeight', 'elCenterY'],
            [0, 1]
          ],
          scale: [
            ['elCenterY-elHeight', 'elCenterY'],
            [0, 1]
          ]
        }
      })
    })
    this.laxInitialized = true
  }

  handleProgressBarUpdate (driverValues, element) {
    const scrollY = driverValues.scrollY[0] - driverValues.scrollY[1]
    if (this.processedScrollY === scrollY) return
    this.processedScrollY = scrollY

    const height =
      this.calculateHeight(this.scaleTarget.getBoundingClientRect(), driverValues.scrollY[1])
    element.style.height = `${height}px`
  }

  calculateHeight ({ y, height }, inertiaValue) {
    const screenHeight = window.innerHeight
    const relativeScrollY = ((y - inertiaValue * this.constructor.inertia) - (screenHeight / 2)) * -1

    if (relativeScrollY <= 0) return 0
    if (relativeScrollY >= height) return height

    return relativeScrollY
  }

  get preInitElement () {
    return this.hasPreInitElementSelectorValue ? document.querySelector(this.preInitElementSelectorValue) : null
  }

  get scaleStartPx () {
    const startRect = this.scaleStartRect

    return (
      startRect[startRect.element.dataset.align || this.align] +
      this.checkpointRects[0].heightCenter
    )
  }

  get scaleStartRect () {
    return this.anchorRects[0]
  }

  get scaleEndPx () {
    if (this.scaleEndRect) {
      const { scaleEndRect } = this

      return (
        scaleEndRect[scaleEndRect.element.dataset.align || this.align] -
        this.scaleStartPx
      )
    } else {
      const lastCheckpointRect = this.anchorRects[this.lastIndex]

      return (
        lastCheckpointRect[lastCheckpointRect.element.dataset.align || this.align] +
        this.checkpointRects[this.lastIndex].heightCenter -
        this.scaleStartPx
      )
    }
  }

  get scaleEndRect () {
    return this.hasScaleEndTarget ? new Rect(this.scaleEndTarget, this.rect) : null
  }

  get lastIndex () {
    return this.anchorRects.length - 1
  }

  get align () {
    return this.hasAlignValue ? this.alignValue : 'verticalCenter'
  }
}
